import { MMButton, SidePanel } from '@components'
import cn from '@integrations/classnames'
import { formatDisplayDateTime, formatExchangeDescription } from '@utils'
import {
  formatTransactionDescription,
  getTransactionStatusName,
  TransactionDirections,
  TransactionStatuses,
  useCurrencies
} from 'mmfintech-backend-api'
import type { TransactionViewPortal } from 'mmfintech-backend-api/types'
import { fetchLastChunk, formatMoney, isValidString, tr } from 'mmfintech-commons'

interface Props {
  transaction: TransactionViewPortal
  onClose: () => void
  onOpenDetails: (transactionId: number) => void
  /** Rendered inside a list card (phone / tablet): just the content, no surface or header of its own. */
  embedded?: boolean
}

/** Side panel for the selected row: the facts already on the row, plus a route to the full record. */
function TransactionPanel({ transaction, onClose, onOpenDetails, embedded = false }: Props) {
  const { getCurrencyPrecision } = useCurrencies()

  const {
    id,
    foreignTransactionId,
    reqTime,
    accountName,
    paymentMethod,
    trnTypeLocalizationKey,
    reference,
    amount,
    currency,
    status,
    direction,
    statusCodeLocalizationKey,
    statusCodeDefaultDescription
  } = transaction

  const signedAmount = amount * (direction === TransactionDirections.WITHDRAW ? -1 : 1)
  const failReason = isValidString(statusCodeLocalizationKey)
    ? tr(statusCodeLocalizationKey, statusCodeDefaultDescription)
    : statusCodeDefaultDescription
  const exchangeDescription = formatExchangeDescription(transaction)

  const fields = [
    { key: 'id', label: tr('FRONTEND.TRANSACTIONS.COLUMNS.ID', 'ID'), value: String(id), copy: true },
    {
      key: 'foreign-id',
      label: tr('FRONTEND.TRANSACTIONS.COLUMNS.FOREIGN_ID', 'Provided ID'),
      value: foreignTransactionId,
      copy: true
    },
    {
      key: 'date',
      label: tr('FRONTEND.TRANSACTIONS.COLUMNS.DATE', 'Time'),
      value: formatDisplayDateTime(reqTime, ' · ')
    },
    { key: 'account', label: tr('FRONTEND.TRANSACTIONS.COLUMNS.ACCOUNT', 'Account'), value: accountName },
    {
      key: 'method',
      label: tr('FRONTEND.TRANSACTIONS.COLUMNS.PAYMENT_METHOD', 'Payment Method'),
      value: paymentMethod ? tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod) : ''
    },
    {
      key: 'description',
      label: tr('FRONTEND.TRANSACTIONS.COLUMNS.DESCRIPTION', 'Description'),
      value: formatTransactionDescription(transaction)
    },
    {
      key: 'reference',
      label: tr('FRONTEND.TRANSACTIONS.COLUMNS.REFERENCE', 'Reference'),
      value: reference,
      copy: true
    },
    {
      key: 'amount',
      label: tr('FRONTEND.TRANSACTIONS.COLUMNS.AMOUNT', 'Amount'),
      value: formatMoney(signedAmount, currency, getCurrencyPrecision(currency))
    },
    {
      key: 'status',
      label: tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS', 'Status'),
      value: getTransactionStatusName(status)
    },
    ...(status === TransactionStatuses.FAILED && failReason
      ? [
          {
            key: 'fail-reason',
            label: tr('FRONTEND.TRANSACTIONS.COLUMNS.STATUS_DESCRIPTION', 'Fail reason'),
            value: failReason
          }
        ]
      : [])
  ].filter((field) => isValidString(field.value))

  function renderValue(key: string, value: string) {
    if (key !== 'description' || !exchangeDescription) return value

    return (
      <span>
        {exchangeDescription.summary}
        <br />
        {exchangeDescription.rate}
      </span>
    )
  }

  const Wrapper = embedded ? 'div' : SidePanel

  return (
    <Wrapper
      className={cn(embedded ? 'transaction-panel-embedded' : 'transaction-panel')}
      data-test='transaction-panel'
    >
      {!embedded && (
        <SidePanel.Header
          eyebrow={tr('FRONTEND.TRANSACTIONS.PANEL.TITLE', 'Transaction')}
          title={tr(trnTypeLocalizationKey, fetchLastChunk(trnTypeLocalizationKey))}
          onClose={onClose}
          closeTestId='transaction-panel-close'
        />
      )}
      <SidePanel.Body>
        <SidePanel.Section title={tr('FRONTEND.TRANSACTIONS.PANEL.DETAILS', 'Details')}>
          {fields.map(({ key, label, value, copy }) => (
            <SidePanel.Row
              key={key}
              label={label}
              copyValue={copy ? value : undefined}
              tone={
                key === 'status' && status === TransactionStatuses.PROCESSED
                  ? 'success'
                  : key === 'status' && status === TransactionStatuses.FAILED
                    ? 'error'
                    : 'default'
              }
              data-test={`panel-${key}`}
            >
              {renderValue(key, value)}
            </SidePanel.Row>
          ))}
        </SidePanel.Section>

        <div>
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.TRANSACTIONS.PANEL.FULL_DETAILS', 'View full details')}
            onClick={() => onOpenDetails(id)}
            data-test='transaction-panel-details'
          />
        </div>
      </SidePanel.Body>
    </Wrapper>
  )
}

export default TransactionPanel
